import {DatePipe} from '@angular/common';
import {inject, Injectable} from '@angular/core';
import { da } from 'date-fns/locale';
import {DamageReportShowModel} from 'src/packages/damage-reporting/data/models/_show/damage-report.show-model';
import {DamageListItemModel} from 'src/packages/damage-reporting/data/models/_view/damage-list-item.model';
import {DamageReportDamageModel} from 'src/packages/damage-reporting/data/models/damage-report.damage.model';
import {DamageStatePipe} from 'src/packages/damage-reporting/pipes/damage-state.pipe';
import {DamageWeightPipe} from 'src/packages/damage-reporting/pipes/damage-weight.pipe';

/**
 * Responsible for converting backend-provided {@link DamageReportShowModel} instances
 * into {@link DamageListItemModel} objects, ensuring each individual damage is transformed
 * into its own separate entry.
 */
@Injectable()
export class DamageReportMapperService {

    private readonly datePipe = inject(DatePipe);
    private readonly damageStatePipe = inject(DamageStatePipe);
    private readonly damageWeightPipe = inject(DamageWeightPipe);
    /**
     * Converts an array of {@link DamageReportShowModel} objects into an array of
     * {@link DamageListItemModel} objects. If a single {@link DamageReportShowModel} contains
     * multiple damages, this method generates multiple {@link DamageListItemModel} instances—
     * one for each damage.
     *
     * @param damageReports - The list of damage reports from the backend.
     * @returns An array of {@link DamageListItemModel} objects. Each object corresponds to a single damage.
     */
    map(damageReports: DamageReportShowModel[]): DamageListItemModel[] {
        const transformedData: DamageListItemModel[] = [];

        // For each report in the array
        damageReports.forEach(damageReport => {
            // Only process if there are damages
            if (damageReport.damages && damageReport.damages.length > 0) {
                // Split each damage into its own DamageReportViewModel
                damageReport.damages.forEach(damage => {
                    const damageReportCopy: DamageListItemModel = {
                        category: damage.category?.name ?? '',
                        weight: this.damageWeightPipe.transform(damage.weight) ?? '',
                        id: damageReport.id ?? 0,
                        damage_id: damage.id ?? 0,
                        title: damage.title,
                        machine: damageReport.machine?.inventory_number ?? '',
                        feature: damage.feature ? damage.feature.serial_number : '-',
                        created_time: damage.created_time,
                        created_by: `${damageReport.created_by?.firstname} ${damageReport.created_by?.lastname} ${damageReport.created_by?.customer_id ? '(Kunde)' : ''}` ?? '',
                        changed_time: this.getLastChangedTime(damageReport, damage),
                        state_text: this.damageStatePipe.transform(damage.state, 'ionIcon') ?? '',
                        state: damage.state
                    };
                    transformedData.push(damageReportCopy);
                });
            }
        });

        return transformedData;
    }


    /**
     * Determines the last changed time of a damage.
     * - The last changed time is the most recent time of the following:
     *  - The time when the damage was created on the client-side
     *  - The time when the damage was created on the server-side
     *  - The time when the damage was last changed
     * @param damageReport - The damage report the damage belongs to
     * @param damage - The damage to determine the last changed time for
     * @private
     */
    private getLastChangedTime(damageReport: DamageReportShowModel, damage: DamageReportDamageModel): Date {
        let lastChangedTime: Date = damageReport.created_at ?? new Date(0);
        if (damageReport.updated_at) lastChangedTime = new Date(damageReport.updated_at);
        if (damage.created_time) lastChangedTime = damage.created_time;
        if (damage.changed_time) lastChangedTime = damage.changed_time;
        return new Date(lastChangedTime);
    }
}